import {
  dateArrayToDate,
  getFullTranslatedDate,
} from '@openeducation/oe-rn-services';
import type {
  HistoryLicense,
  Language,
} from '@openeducation/oe-rn-shared-types';
import {
  secondaryFontFamily,
  styled,
  ThemeType,
} from '@openeducation/oe-rn-themes';
import React, { memo, useMemo } from 'react';
import { View } from 'react-native';

import { OEText } from '../../atoms/oe-text/oe-text';
import { mb } from '../../style-utils';

export interface LicenseHistoryListItemProps extends HistoryLicense {
  labels: {
    from: string;
    to: string;
    since: string;
  };
  isActive?: boolean;
  language: Language;
  accentColor?: string;
}

const LicenseHistoryListItem = ({
  labels,
  isActive,
  name: licenseName,
  startDate,
  endDate,
  language,
  accentColor,
}: LicenseHistoryListItemProps) => {
  const startDisplayDate = startDate
    ? getFullTranslatedDate(dateArrayToDate(startDate)!, language)
    : '';
  const endDisplayDate = endDate
    ? getFullTranslatedDate(dateArrayToDate(endDate)!, language)
    : '';

  const dateText = useMemo(() => {
    if (isActive) {
      return `${labels.since} ${startDisplayDate}`;
    }
    return `${labels.from} ${startDisplayDate} ${labels.to} ${endDisplayDate}`;
  }, [labels, isActive, startDisplayDate, endDisplayDate]);

  return (
    <View style={[mb(12)]}>
      <TimelineBullet isActive={isActive} accentColor={accentColor} />
      <Container isActive={isActive} accentColor={accentColor}>
        <Title>{licenseName}</Title>
        <DateText isActive={isActive} numberOfLines={2}>
          {dateText}
        </DateText>
      </Container>
    </View>
  );
};

type ContainerType = {
  isActive?: boolean;
  accentColor?: string;
};

const Container = styled.View<ContainerType>`
  border-radius: 4px;
  padding: 12px;
  border-width: 1px;
  border-color: ${({ theme, isActive, accentColor }) =>
    isActive ? accentColor || theme.color.primary : theme.color.border};
  background-color: ${({ theme, isActive }) =>
    isActive ? theme.color.blueMediumJr : theme.color.backgroundGraySecondary};
  margin-right: 12px;
  margin-left: 20px;
`;

const Title = styled(OEText).attrs(({ theme }) => ({
  color: theme.color.black,
  fontFamily: secondaryFontFamily,
  fontSize: 16,
}))`
  margin-bottom: 4px;
`;

type DateTextType = {
  theme: ThemeType;
  isActive?: boolean;
};

const DateText = styled(OEText).attrs(({ theme, isActive }: DateTextType) => ({
  color: isActive ? theme.color.dark1 : theme.color.dark3,
  fontFamily: secondaryFontFamily,
  fontSize: 12,
}))<DateTextType>``;

const BULLET_SIZE = 12;

type TimelineBulletType = {
  isActive?: boolean;
  accentColor?: string;
};

const TimelineBullet = styled.View<TimelineBulletType>`
  width: ${BULLET_SIZE}px;
  height: ${BULLET_SIZE}px;
  border-radius: ${BULLET_SIZE / 2}px;
  border-width: 1px;
  border-color: ${({ theme, accentColor }) =>
    accentColor || theme.color.primary};
  background-color: ${({ theme, isActive, accentColor }) =>
    isActive ? theme.color.blueMediumJr : accentColor || theme.color.primary};
  position: absolute;
  left: 0px;
  top: 0;
  z-index: 1;
`;

export default memo(LicenseHistoryListItem);
